/** A table preview is bounded independently of the source preview's byte limit. */
export function filePreviewDelimiter(file: { name: string; mimeType?: string }): "," | "\t" | null {
  const mime = file.mimeType?.split(";", 1)[0]?.trim().toLowerCase();
  if (mime === "text/csv") return ",";
  if (mime === "text/tab-separated-values") return "\t";
  if (mime && mime !== "text/plain" && mime !== "application/octet-stream") return null;
  if (/\.csv$/i.test(file.name)) return ",";
  if (/\.tsv$/i.test(file.name)) return "\t";
  return null;
}

export interface DelimitedLimits {
  /** Rows kept; parsing stops after this many. */
  readonly maxRows: number;
  /** Cells kept per row; the rest are dropped. */
  readonly maxColumns: number;
  /** Characters kept per cell; the rest are dropped. */
  readonly maxCellLength: number;
}

const UNLIMITED: DelimitedLimits = {
  maxRows: Number.POSITIVE_INFINITY,
  maxColumns: Number.POSITIVE_INFINITY,
  maxCellLength: Number.POSITIVE_INFINITY,
};

/**
 * Parse CSV or TSV the way spreadsheets write it: quoted delimiters, escaped
 * quotes ("") and multiline cells. A quote opens a quoted cell only at the
 * cell's start. `truncated` is true when a limit dropped text or a quote was
 * left open.
 */
export function parseDelimited(
  text: string,
  delimiter: "," | "\t",
  limits: DelimitedLimits = UNLIMITED,
): { rows: string[][]; truncated: boolean } {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  let truncated = false;
  const endCell = () => {
    if (row.length < limits.maxColumns) row.push(cell);
    else truncated = true;
    cell = "";
  };
  for (let index = text.charCodeAt(0) === 0xfeff ? 1 : 0; index < text.length; index++) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') {
        if (cell.length < limits.maxCellLength) cell += '"';
        else truncated = true;
        index++;
        continue;
      }
      if (quoted || cell === "") {
        quoted = !quoted;
        continue;
      }
    }
    if (!quoted && (char === delimiter || char === "\n" || char === "\r")) {
      endCell();
      if (char !== delimiter) {
        rows.push(row);
        row = [];
        if (char === "\r" && text[index + 1] === "\n") index++;
        if (rows.length === limits.maxRows)
          return { rows, truncated: truncated || index < text.length - 1 };
      }
    } else if (cell.length < limits.maxCellLength) cell += char;
    else truncated = true;
  }
  if (cell.length || row.length) {
    endCell();
    rows.push(row);
  }
  return { rows, truncated: truncated || quoted };
}

/** Bounds of the table preview. Rows are not bounded: the table renders them a page at a time. */
export const DELIMITED_PREVIEW_LIMITS: DelimitedLimits = {
  maxRows: Number.POSITIVE_INFINITY,
  maxColumns: 100,
  maxCellLength: 2000,
};

/** Rows the table renders first, and adds each time the reader scrolls near its end. */
export const DELIMITED_PREVIEW_PAGE_ROWS = 200;

/** Preserve quoted delimiters, escaped quotes and multiline cells; raw mode retains all text. */
export function parseDelimitedPreview(text: string, delimiter: "," | "\t") {
  return parseDelimited(text, delimiter, DELIMITED_PREVIEW_LIMITS);
}

/** How many of `total` rows to render after the reader reaches the end of `shown`. */
export function nextDelimitedPreviewRows(
  shown: number,
  total: number,
  page: number = DELIMITED_PREVIEW_PAGE_ROWS,
): number {
  return Math.min(total, Math.max(0, shown) + page);
}
